// src/components/UserManagement.js
//
// AppAdmin-only screen. Manages users WITHIN the AppAdmin's own tenant.

import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Paper, Box, TextField, Button, Grid,
  FormControl, Select, MenuItem, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Fade, Slide, useTheme, alpha, Alert, CircularProgress,
  Chip,
} from '@mui/material';
import { Delete, Edit } from '@mui/icons-material';
import {
  collection, addDoc, getDocs, query, where, deleteDoc, doc, updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore } from './firebase.config';
import { useAuth } from '../auth/AuthContext';

const blankForm = {
  userName: '',
  userEmailId: '',
  userMobileNo: '',
  role: 'AppUser',
};

const UserManagement = () => {
  const theme = useTheme();
  const {
    currentUser,
    isAppAdmin,
    tenantId,
    tenantName,
    profileDocId,
    loading: authLoading,
  } = useAuth();

  const [formData, setFormData] = useState(blankForm);
  const [users, setUsers] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [lastAction, setLastAction] = useState('');

  const fetchUsers = useCallback(async () => {
    if (!tenantId) return;
    try {
      const q = query(
        collection(firestore, 'UserProfileMaster'),
        where('tenantId', '==', tenantId)
      );
      const snap = await getDocs(q);
      setUsers(
        snap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }))
      );
    } catch (err) {
      console.error('Error fetching users:', err);
      setErrors({ submission: 'Failed to load users.' });
    }
  }, [tenantId]);

  useEffect(() => {
    if (isAppAdmin && tenantId) fetchUsers();
  }, [isAppAdmin, tenantId, fetchUsers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const t = {};
    if (!formData.userName.trim()) t.userName = 'User Name is required';
    if (!formData.userEmailId.trim()) {
      t.userEmailId = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.userEmailId)) {
      t.userEmailId = 'Invalid email format';
    }
    if (!['AppAdmin', 'AppUser', 'Verifier'].includes(formData.role)) {
      t.role = 'Invalid role';
    }
    setErrors(t);
    return Object.keys(t).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const email = formData.userEmailId.trim().toLowerCase();
    const name = formData.userName.trim();
    const mobile = formData.userMobileNo.trim();

    const superAdmins = [
      process.env.REACT_APP_ADMIN_EMAIL1,
      process.env.REACT_APP_ADMIN_EMAIL2,
      process.env.REACT_APP_ADMIN_EMAIL3,
    ]
      .filter(Boolean)
      .map((s) => s.toLowerCase().trim());
    if (superAdmins.includes(email)) {
      setErrors({
        userEmailId: 'This email is reserved for a platform SuperAdmin and cannot be added here.',
      });
      return;
    }

    setIsSubmitting(true);
    setErrors({});

    try {
      if (selectedItem) {
        if (selectedItem.id === profileDocId) {
          setErrors({ submission: 'You cannot edit your own profile from here.' });
          setIsSubmitting(false);
          return;
        }
        await updateDoc(doc(firestore, 'UserProfileMaster', selectedItem.id), {
          email,
          displayName: name,
          mobileNo: mobile,
          role: formData.role,
          updatedBy: currentUser.email,
          updatedAt: serverTimestamp(),
        });
      } else {
        const dup = await getDocs(
          query(collection(firestore, 'UserProfileMaster'), where('email', '==', email))
        );
        if (!dup.empty) {
          setErrors({ userEmailId: 'A user with this email already exists.' });
          setIsSubmitting(false);
          return;
        }

        await addDoc(collection(firestore, 'UserProfileMaster'), {
          email,
          displayName: name,
          mobileNo: mobile,
          role: formData.role,
          tenantId,
          tenantName,
          status: 'active',
          createdBy: currentUser.email,
          createdAt: serverTimestamp(),
          updatedBy: currentUser.email,
          updatedAt: serverTimestamp(),
        });
      }

      setFormData(blankForm);
      setSelectedItem(null);
      setLastAction(selectedItem ? 'update' : 'create');
      setSubmitSuccess(true);
      await fetchUsers();
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving user:', err);
      setErrors({ submission: 'Operation failed. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (user) => {
    if (user.id === profileDocId) {
      setErrors({ submission: 'You cannot edit your own profile.' });
      return;
    }
    setSelectedItem(user);
    setFormData({
      userName: user.displayName || '',
      userEmailId: user.email || '',
      userMobileNo: user.mobileNo || '',
      role: user.role || 'AppUser',
    });
    setErrors({});
  };

  const handleCancelEdit = () => {
    setSelectedItem(null);
    setFormData(blankForm);
    setErrors({});
  };

  const handleDelete = async (user) => {
    if (user.id === profileDocId) {
      setErrors({ submission: 'You cannot delete yourself.' });
      return;
    }
    if (!window.confirm(`Delete user "${user.displayName}"? This cannot be undone.`)) return;
    try {
      setIsSubmitting(true);
      await deleteDoc(doc(firestore, 'UserProfileMaster', user.id));
      setLastAction('delete');
      setSubmitSuccess(true);
      await fetchUsers();
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (err) {
      console.error('Error deleting user:', err);
      setErrors({ submission: 'Failed to delete. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (authLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAppAdmin) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Alert severity="error">
          You do not have permission to access User Management. This page is for tenant administrators only.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          p: 3,
          minHeight: '100vh',
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.secondary.main, 0.05)})`,
        }}
      >
        <Fade in timeout={800}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              variant="h3"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              User Management
            </Typography>
          </Box>
        </Fade>

        {submitSuccess && (
          <Fade in timeout={600}>
            <Alert severity="success" sx={{ mb: 3 }}>
              {lastAction === 'delete'
                ? 'User deleted successfully.'
                : lastAction === 'update'
                  ? 'User updated successfully.'
                  : 'User added successfully.'}
            </Alert>
          </Fade>
        )}

        {errors.submission && (
          <Fade in timeout={600}>
            <Alert severity="error" sx={{ mb: 3 }}>{errors.submission}</Alert>
          </Fade>
        )}

        <Slide direction="up" in timeout={600}>
          <Paper
            sx={{
              p: 4, mb: 3,
              background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)}, ${alpha(theme.palette.background.paper, 0.95)})`,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              borderRadius: 3,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
            }}
          >
            <Typography variant="h6" gutterBottom>
              {selectedItem ? 'Edit User' : 'Add New User'}
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.userName}>
                    <TextField
                      label="User Name"
                      name="userName"
                      value={formData.userName}
                      onChange={handleChange}
                      required
                      error={!!errors.userName}
                      helperText={errors.userName}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.role}>
                    <InputLabel>Role</InputLabel>
                    <Select
                      name="role"
                      value={formData.role}
                      onChange={handleChange}
                      label="Role"
                      required
                    >
                      <MenuItem value="AppUser">App User</MenuItem>
                      <MenuItem value="AppAdmin">App Admin</MenuItem>
                      <MenuItem value="Verifier">Verifier</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth error={!!errors.userEmailId}>
                    <TextField
                      label="User Email"
                      name="userEmailId"
                      type="email"
                      value={formData.userEmailId}
                      onChange={handleChange}
                      required
                      error={!!errors.userEmailId}
                      helperText={errors.userEmailId}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Mobile No"
                      name="userMobileNo"
                      value={formData.userMobileNo}
                      onChange={handleChange}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    sx={{ mr: 2 }}
                  >
                    {isSubmitting ? 'Saving...' : selectedItem ? 'Update User' : 'Add User'}
                  </Button>
                  {selectedItem && (
                    <Button variant="outlined" onClick={handleCancelEdit}>
                      Cancel Edit
                    </Button>
                  )}
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Slide>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const isSelf = user.id === profileDocId;
                return (
                  <TableRow key={user.id}>
                    <TableCell>
                      {user.displayName}
                      {isSelf && (
                        <Chip label="You" size="small" sx={{ ml: 1 }} color="info" />
                      )}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell>{user.status}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={isSelf ? 'Cannot edit yourself' : 'Edit'}>
                        <span>
                          <IconButton
                            color="primary"
                            onClick={() => handleEdit(user)}
                            disabled={isSelf}
                          >
                            <Edit />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={isSelf ? 'Cannot delete yourself' : 'Delete'}>
                        <span>
                          <IconButton
                            color="error"
                            onClick={() => handleDelete(user)}
                            disabled={isSelf}
                          >
                            <Delete />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No users in this tenant yet.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Container>
  );
};

export default UserManagement;